import {
  TenantPaymentsSettings,
  TenantPartnersSettings,
} from "@/types/payments";
import { CatalogTenant } from "@/types/tenant-catalog";
import type {
  TenantProfile,
  UpdateTenantProfileDto,
} from "@/types/tenant-profile";
import { BaseService } from "./BaseService";
import { ITenantAdminService } from "./interfaces/ITenantAdminService";
import {
  AssignmentResultDto,
  CustomRoleListQuery,
  CreateTenantUserDto,
  CreateTenantCustomRoleDto,
  CreateFrontDeskUserDto,
  CreateHotelAdminUserDto,
  TenantCustomRole,
  UserCreationResponse,
  UserFilterOptions,
  UserCustomRoleAssignmentDto,
  UpdateTenantCustomRoleDto,
  UpdateUserDto,
  UserFilters,
} from "@/types/user";
import { UserProfile } from "@/types/user-profile";
import {
  AccessGroupDetail,
  AccessGroupFilterOptions,
  AccessGroupListQuery,
  CreateAccessGroupDto,
  PaginatedAccessGroupResponse,
  UpdateAccessGroupDto,
} from "@/types/access-group";
import type { UsersKpiFilters, UsersKpiSummary } from "@/types/users-kpis";

class TenantAdminService extends BaseService implements ITenantAdminService {
  private basePath = "/tenant-admin/users";
  private adminBasePath = "/admin/user";

  constructor() {
    super();
  }

  private mapTenantUsersFilters(
    filters?: UserFilters,
  ): UserFilters | undefined {
    if (!filters) {
      return undefined;
    }

    const mappedFilters = { ...filters } as UserFilters & { name?: string };

    // tenant-admin users endpoints expect "name" query instead of "search"
    const trimmedSearch = filters.search?.trim();
    if (trimmedSearch) {
      mappedFilters.name = trimmedSearch;
    }
    delete mappedFilters.search;

    // tenant-admin users endpoints expect "sortField" instead of "sortBy"
    if (filters.sortBy && !filters.sortField) {
      mappedFilters.sortField = filters.sortBy;
    }
    delete mappedFilters.sortBy;

    return mappedFilters;
  }

  async createHotelAdminUser(
    userData: CreateHotelAdminUserDto,
  ): Promise<UserProfile> {
    return this.post<UserProfile>(`${this.basePath}/hotel-admin`, userData);
  }

  async createFrontDeskUser(
    userData: CreateFrontDeskUserDto,
  ): Promise<UserProfile> {
    return this.post<UserProfile>(`${this.basePath}/front-desk`, userData);
  }

  async createUser(
    userData: CreateTenantUserDto,
  ): Promise<UserCreationResponse> {
    return this.post<UserCreationResponse>(this.basePath, userData);
  }

  async getUsers(filters?: UserFilters): Promise<UserProfile[]> {
    if (filters?.tenantId) {
      return this.get<UserProfile[]>(
        this.adminBasePath,
        this.mapTenantUsersFilters(filters),
      );
    }

    return this.get<UserProfile[]>(
      this.basePath,
      this.mapTenantUsersFilters(filters),
    );
  }

  async getUsersCount(filters?: UserFilters): Promise<number> {
    if (filters?.tenantId) {
      return this.get<number>(
        `${this.adminBasePath}/count`,
        this.mapTenantUsersFilters(filters),
      );
    }

    return this.get<number>(
      `${this.basePath}/count`,
      this.mapTenantUsersFilters(filters),
    );
  }

  async getUsersKpis(filters?: UsersKpiFilters): Promise<UsersKpiSummary> {
    return this.get<UsersKpiSummary>(`${this.basePath}/kpis`, filters);
  }

  async getUserFilterOptions(
    filters?: UserFilters,
  ): Promise<UserFilterOptions> {
    return this.get<UserFilterOptions>(
      `${this.basePath}/filter-options`,
      this.mapTenantUsersFilters(filters),
    );
  }

  async getUserById(id: string): Promise<UserProfile> {
    return this.get<UserProfile>(`${this.basePath}/${id}`);
  }

  async updateUser(id: string, userData: UpdateUserDto): Promise<void> {
    return this.patch<void>(`${this.basePath}/${id}`, userData);
  }

  async deleteUser(id: string): Promise<void> {
    return this.delete<void>(`${this.basePath}/${id}`);
  }

  async getCustomRoles(
    query?: CustomRoleListQuery,
  ): Promise<TenantCustomRole[]> {
    return this.get<TenantCustomRole[]>("/tenant-admin/custom-roles", query);
  }

  async createCustomRole(
    payload: CreateTenantCustomRoleDto,
  ): Promise<TenantCustomRole> {
    return this.post<TenantCustomRole>("/tenant-admin/custom-roles", payload);
  }

  async updateCustomRole(
    id: string,
    payload: UpdateTenantCustomRoleDto,
  ): Promise<TenantCustomRole> {
    return this.patch<TenantCustomRole>(
      `/tenant-admin/custom-roles/${id}`,
      payload,
    );
  }

  async deleteCustomRole(id: string): Promise<void> {
    return this.delete<void>(`/tenant-admin/custom-roles/${id}`);
  }

  async replaceUserCustomRoles(
    userId: string,
    customRoleIds: string[],
  ): Promise<AssignmentResultDto> {
    return this.put<AssignmentResultDto>(
      `/tenant-admin/users/${userId}/custom-roles`,
      {
        customRoleIds,
      },
    );
  }

  async getUserCustomRoles(
    userId: string,
  ): Promise<UserCustomRoleAssignmentDto[]> {
    return this.get<UserCustomRoleAssignmentDto[]>(
      `/tenant-admin/users/${userId}/custom-roles`,
    );
  }

  async addUserCustomRole(
    userId: string,
    roleId: string,
  ): Promise<AssignmentResultDto> {
    return this.post<AssignmentResultDto>(
      `/tenant-admin/users/${userId}/custom-roles/${roleId}`,
      {},
    );
  }

  async removeUserCustomRole(
    userId: string,
    roleId: string,
  ): Promise<AssignmentResultDto> {
    return this.delete<AssignmentResultDto>(
      `/tenant-admin/users/${userId}/custom-roles/${roleId}`,
    );
  }

  async getAccessGroups(
    query?: AccessGroupListQuery,
  ): Promise<PaginatedAccessGroupResponse> {
    return this.get<PaginatedAccessGroupResponse>(
      "/tenant-admin/access-groups",
      query,
    );
  }

  async getAccessGroupFilterOptions(): Promise<AccessGroupFilterOptions> {
    return this.get<AccessGroupFilterOptions>(
      "/tenant-admin/access-groups/filter-options",
    );
  }

  async getAccessGroupById(id: string): Promise<AccessGroupDetail> {
    return this.get<AccessGroupDetail>(`/tenant-admin/access-groups/${id}`);
  }

  async createAccessGroup(
    payload: CreateAccessGroupDto,
  ): Promise<AccessGroupDetail> {
    return this.post<AccessGroupDetail>("/tenant-admin/access-groups", payload);
  }

  async updateAccessGroup(
    id: string,
    payload: UpdateAccessGroupDto,
  ): Promise<AccessGroupDetail> {
    return this.patch<AccessGroupDetail>(
      `/tenant-admin/access-groups/${id}`,
      payload,
    );
  }

  async deleteAccessGroup(id: string): Promise<void> {
    return this.delete<void>(`/tenant-admin/access-groups/${id}`);
  }

  async replaceAccessGroupMembers(
    id: string,
    userIds: string[],
  ): Promise<AccessGroupDetail> {
    return this.put<AccessGroupDetail>(
      `/tenant-admin/access-groups/${id}/members`,
      {
        userIds,
      },
    );
  }

  async replaceAccessGroupHotels(
    id: string,
    hotelIds: string[],
  ): Promise<AccessGroupDetail> {
    return this.put<AccessGroupDetail>(
      `/tenant-admin/access-groups/${id}/hotels`,
      {
        hotelIds,
      },
    );
  }

  async getTenant(tenantId: string): Promise<CatalogTenant> {
    return this.get<CatalogTenant>(`/tenants/${tenantId}`);
  }

  async getTenantProfile(): Promise<TenantProfile> {
    return this.get<TenantProfile>("/tenant/profile");
  }

  async updateTenantProfile(
    payload: UpdateTenantProfileDto,
  ): Promise<TenantProfile> {
    return this.patch<TenantProfile>("/tenant/profile", payload);
  }

  async getTenantLogoUrl(): Promise<string> {
    return this.get<string>("/tenant/logo", undefined, { disableToast: true });
  }

  async requestTenantLogoUpload(contentType: string): Promise<{
    uploadUrl: string;
    logoKey: string;
  }> {
    return this.post<{ uploadUrl: string; logoKey: string }>("/tenant/logo", {
      contentType,
    });
  }

  async getTenantPaymentSettings(
    tenantId: string,
  ): Promise<TenantPaymentsSettings> {
    return this.get<TenantPaymentsSettings>(`/tenants/${tenantId}/payments`);
  }

  async updateTenantPaymentSettings(
    tenantId: string,
    settings: {
      paymentsEnabled: boolean;
    },
  ): Promise<void> {
    return this.patch<void>(`/tenants/${tenantId}/payments`, settings);
  }

  async getTenantPartnersSettings(
    tenantId: string,
  ): Promise<TenantPartnersSettings> {
    return this.get<TenantPartnersSettings>(`/tenants/${tenantId}/partners`);
  }

  async updateTenantPartnersSettings(
    tenantId: string,
    settings: {
      enabled: boolean;
    },
  ): Promise<void> {
    return this.patch<void>(`/tenants/${tenantId}/partners`, settings);
  }
}

export const tenantAdminService = new TenantAdminService();
